// Used in oauth-guard.service.ts, login-form.effect.ts, teacher-class.component.ts, home-page-container.component.ts, student-profile.component.ts, demo-teacher-student-toggle.component.ts

// Angular
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";

// Services/Types/Helpers
import { LocalStorage } from "#core/services";
import { ServiceContextService } from "@carnegie-learning/data-access-angular";
import { Course } from "cli-types";
import { TimezoneHelper } from "./timezone.helper";

// Moment
import { ANALYTICS_SERVICE } from "@carnegie-learning/data-access-angular-mathia";
import moment from "moment";
import { AUTHENTICATION_SERVICE, ClToken2Service } from "@carnegie-learning/data-access-angular-fido";
import { AuthenticationService } from "@carnegie-learning/data-access-fido";
import { Observable, map, tap } from "rxjs";
import { LaunchIntentService } from "#core/services/launch-intent.service";

@Injectable({ providedIn: "root" })
export class AuthHelper {
    readonly DISTRIBUTOR = 1;
    readonly DISTRICTADMIN = 2;
    readonly SCHOOLADMIN = 3;
    readonly TEACHER = 4;
    readonly STUDENT = 5;

    constructor(
        private localStorage: LocalStorage,
        private router: Router,
        private serviceContext: ServiceContextService,
        private timezoneHelper: TimezoneHelper,
        @Inject(AUTHENTICATION_SERVICE) private authService: AuthenticationService,
        private clToken2Service: ClToken2Service,
        private launchIntentService: LaunchIntentService
    ) {}

    /**
     * @method redirectUser
     * @description based on roleLevel of user object, redirects user to corresponding routes
     * @param user
     */
    redirectUser(user: any): void {
        if (this.router.url.includes("oauth/login")) {
            this.router.navigate(["/oauth/authorize"], {
                queryParamsHandling: "preserve",
            });
            return;
        }

        // During LMSI/LTI launch, let launch-context + intent guards own navigation.
        // Forcing generic role redirects here can bounce routes and re-trigger whoami.
        if (this.isLtiLaunchFlowActive()) {
            return;
        }

        switch (user.roleLevel) {
            case this.DISTRIBUTOR:
                this.router.navigate(["/distributor"]);
                break;
            case this.DISTRICTADMIN:
                this.router.navigate(["/teacher"]); // TODO change this when new views are made
                break;
            case this.SCHOOLADMIN:
                this.router.navigate(["/teacher"]); // TODO change this when new views are made
                break;
            case this.TEACHER:
                if (this.router.url.includes("temp-password/form")) {
                    // When resetting password, payload doesn't include teacher-classes data so we default to teacher-classes view
                    this.router.navigate(["/teacher/classes"]);
                } else {
                    const requestProgramID =
                        this.localStorage.get("requestProgramID");
                    if (user.lastCourse !== null && !requestProgramID) {
                        this.router.navigate([
                            `/teacher/classes/${user.lastCourse}`,
                        ]);
                    } else {
                        this.router.navigate(["/teacher/classes"]);
                    }
                }

                break;
            case this.STUDENT:
                if ((<any>window).isDemo) {
                    this.restoreTeacherProfile();
                } else {
                    if (user.lastCourse !== null) {
                        this.router.navigate([
                            `/student/courses/${user.lastCourse}/assignments`,
                        ]);
                    } else {
                        this.router.navigate(["/student"]);
                    }
                }
                break;
            default:
                this.router.navigate(["/login"]);
        }
    }

    private isLtiLaunchFlowActive(): boolean {
        return !!this.launchIntentService.getLaunchId();
    }

    /**
     * @method isAuthenticated
     * @description checks if authentication token has expired or not
     * @returns {boolean}
     */
    isAuthenticated(): boolean {
        const data = this.localStorage.get("authToken", true);

        if (data === undefined || data === null) {
            return false;
        }
        const authExpiration = Number(data[0]);
        return moment.now() < authExpiration;
    }

    /**
     * @method logout
     * @description clears user data from client and redirects user to login page
     */
    logout(): void {
        this.cleanupAnalytics();
        const logoutRedirectURL = this.localStorage.get("logoutRedirectURL");
        this.localStorage.clear();

        // Must be after the clear, url needs to persist
        if (
            ((<any>window).isDemo ||
                (<any>window).env === "s" ||
                (<any>window).env === "local") &&
            logoutRedirectURL !== undefined &&
            logoutRedirectURL !== null &&
            logoutRedirectURL !== "null" &&
            logoutRedirectURL !== ""
        ) {
            this.localStorage.set("logoutRedirectURL", logoutRedirectURL);
        } else if (
            (<any>window).FIDO_PORTAL_RETURN_URL !== undefined &&
            !(<any>window).isDemo
        ) {
            this.localStorage.set(
                "logoutRedirectURL",
                (<any>window).FIDO_PORTAL_RETURN_URL
            );
        }

        this.router.navigate(["/login"]);
    }

    switchToStudentProfile(data: any) {
        // TODO: something other than any? I can't find any indication of what else this should be.
        // Existing User Profile.
        const oldProfile = this.localStorage.get("userProfile");
        const oldFidoProfile = this.localStorage.get("fidoUserProfile");
        const oldToken = this.localStorage.get("authToken");
        const logoutRedirectURL = this.localStorage.get("logoutRedirectURL");
        const teacherCLLaunchCTX = this.localStorage.get("CL_TICKET");
        this.localStorage.clear();

        this.localStorage.set("userProfile_backup", oldProfile);
        this.localStorage.set("fidoUserProfile_backup", oldFidoProfile);
        this.localStorage.set("authToken_backup", oldToken);
        this.localStorage.set("url_backup", this.router.url);

        let date: any = this.timezoneHelper.convertServerDateToUserTZ(
            data.expire_at,
            data.user.timezone
        );
        date = date.toString();
        this.localStorage.set("authToken", data.access_token, date);
        this.localStorage.set("userProfile", data.user);

        // Must be after the clear, url needs to persist
        if (
            ((<any>window).isDemo ||
                (<any>window).env === "s" ||
                (<any>window).env === "local") &&
            logoutRedirectURL !== undefined &&
            logoutRedirectURL !== null &&
            logoutRedirectURL !== "null" &&
            logoutRedirectURL !== ""
        ) {
            this.localStorage.set("logoutRedirectURL", logoutRedirectURL);
        }

        if ((<any>window).isDemo) {
            // Launch context needs to be cleared and stored for student session data to occur
            this.localStorage.set("CL_TICKET_backup", teacherCLLaunchCTX);
            this.localStorage.remove("CL_TICKET"); // remove teacher launch context so student can be set
        }

        this.router.navigate(["/student"], { replaceUrl: true });
    }

    restoreTeacherProfile() {
        const oldProfile = this.localStorage.get("userProfile_backup");
        const oldFidoUserProfile = this.localStorage.get(
            "fidoUserProfile_backup"
        );
        const oldToken = this.localStorage.get("authToken_backup");
        let oldUrl = this.localStorage.get("url_backup");
        const logoutRedirectURL = this.localStorage.get("logoutRedirectURL");
        const teacherCLLaunchCTX = this.localStorage.get("CL_TICKET_backup");

        this.localStorage.clear();

        this.localStorage.set("userProfile", oldProfile);
        this.localStorage.set("fidoUserProfile", oldFidoUserProfile);
        let date: any = this.timezoneHelper.convertServerDateToUserTZ(
            oldProfile.accessTokenExpireAt,
            oldProfile.timezone
        );
        date = date.toString();
        this.localStorage.set("authToken", oldToken, date);

        // Must be after the clear, url needs to persist
        if (
            ((<any>window).isDemo ||
                (<any>window).env === "s" ||
                (<any>window).env === "local") &&
            logoutRedirectURL !== undefined &&
            logoutRedirectURL !== null &&
            logoutRedirectURL !== "null" &&
            logoutRedirectURL !== ""
        ) {
            this.localStorage.set("logoutRedirectURL", logoutRedirectURL);
        }

        if ((<any>window).isDemo) {
            // Due to new toggle teacher needs to get redirected back to classes to avoid data corruption
            oldUrl = "/teacher/classes";

            // Launch context needs to be cleared and stored for student session data to occur
            this.localStorage.set("CL_TICKET", teacherCLLaunchCTX);
            this.localStorage.remove("CL_TICKET_backup");
        }

        this.router.navigate([oldUrl], { replaceUrl: true });
        (<any>window).location.reload();
    }

    refreshClToken2(): Observable<boolean> {
        return this.authService.whoamiV2().pipe(
            tap((response) => {
                if (response?.authToken) {
                    this.clToken2Service.setClToken2(response.authToken);
                }
            }),
            map((response) => !!response?.authToken)
        );
    }

    private cleanupAnalytics() {
        const version = this.localStorage.get("CL_VERSION");
        if (version) {
            this.serviceContext
                .get(ANALYTICS_SERVICE, version)
                .pipe()
                .subscribe({
                    next: (analyticsService) => {
                        if (analyticsService) {
                            // stop resources being viewed
                            // get the selectedCourse
                            const stuAct = <number>(
                                this.localStorage.get("StuAct")
                            );
                            const selectedCourse = <Course>(
                                this.localStorage.get("selectedCourse")
                            );
                            // stop the activity currently in view
                            if (stuAct) {
                                analyticsService.logResourceAccessStop({
                                    id: String(stuAct),
                                    level: "StuAct",
                                });
                            }
                            // stop the class currently in view
                            if (selectedCourse) {
                                analyticsService.logResourceAccessStop({
                                    id: String(selectedCourse.courseID),
                                    level: "StuClass",
                                    name: selectedCourse.courseTitle,
                                });
                            }
                        }
                    },
                    error: (err) =>
                        console.log("error starting analytics service", err),
                });
        } else {
            console.warn("unable to clean up analytics: no version to load");
        }
    }
}
